import { ApolloLink } from '@apollo/client';
import {CartItemBase, CartListItem} from "../shared/ShoppingCart/ShoppingCartContextTypes";
import moment from "moment";

export const authMiddleware = (token: string | null) => new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  return forward(operation);
});

export const removeObjectWithId = (arr: any, id: number) => {
  const newArr = [...arr];
  const objWithIdIndex = newArr.findIndex((obj: any) => obj.id === id);

  if (objWithIdIndex > -1) {
    newArr.splice(objWithIdIndex, 1);
  }

  return newArr;
}

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const groupCartItems = (cart: CartListItem<CartItemBase>[]) => {
  let data: any = [];

  cart.forEach((cartItem: CartListItem<CartItemBase>) => {
    const key = `${cartItem.item.Pharmacy?.legalName}--${cartItem.item.Pharmacy?.id}`;
    if (data[key]) {
      data[key].push(cartItem);
    } else {
      data[key] = [cartItem];
    }
  });

  return data;
}

export const getPharmacyLegalName = (name: string) => {
  const names = name.split('--');

  return names[0];
}

export const formatPrice = (price: string | number) => {
  const formattedPrice = +price;

  return formattedPrice.toFixed(2);
}

export const currencyFormat = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

export const getNDCQuery = (value: string) => {
  const isNDCNumber = /^(?:\d{4}-\d{4}-\d{2}|\d{5}-(?:\d{3}-\d{2}|\d{4}-\d{1,2}))$/.test(value);
  let ndcQuery: string = `packaging.package_ndc:"${value}"`;

  if (isNDCNumber) {
    const items = value.split('-');
    const lastItem = items[items.length - 1];
    const firstItem = items[0];

    if (lastItem.length === 1 && firstItem.length === 5 && firstItem.startsWith('0')) {
      const newValue = `${firstItem.slice(1)}-${items[1]}-0${lastItem}`;
      ndcQuery = `${ndcQuery}+OR+packaging.package_ndc:"${newValue}"`;
    }
  }

  return ndcQuery;
}

export const calculateTransactionFee = (totalAmount: number) => {
  const result = (totalAmount * 0.8) / 100;
  return result < 5 ? result : 5;
};

/** Stripe fee on captured card amount (2.9% + $0.30). */
export const calculateStripeCardFeeOnCapture = (capturedAmount: number) => {
  const rate = 0.029;
  const fixed = 0.3;
  return parseFloat((capturedAmount * rate + fixed).toFixed(2));
};

/** PrimePostRx $5 card surcharge (bundled into displayed card fee, not shown separately). */
export const CARD_PRIMEPOST_SURCHARGE = 5;

/** Total seller card fee (Stripe + surcharge) — matches backend transfer job. */
export const calculateSellerCardProcessingFee = (capturedAmount: number) =>
  parseFloat(
    (calculateStripeCardFeeOnCapture(capturedAmount) + CARD_PRIMEPOST_SURCHARGE).toFixed(2)
  );

/** True only when the order row is tagged CARD. Null/ACH/missing → ACH (never buyer's current payment method). */
export const isCardPaymentOrder = (order: { paymentType?: string | null }) =>
  order?.paymentType === 'CARD';

/** Buyer charge base used for Stripe card fee (product subtotal + shipping buyer pays). */
export const getBuyerCapturedAmountForCardFee = (
  items: { totalAmount: string | number; status?: number; shippingFee?: string }[],
  shippingMethodKey: string,
  isShippingPaidBySeller: boolean
) => {
  const itemsTotal = items
    .filter((item) => item.status === undefined || item.status === 1)
    .reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
  const sellerPaysShipping =
    shippingMethodKey === 'fedex-ground' || isShippingPaidBySeller;
  const buyerShipping = sellerPaysShipping
    ? 0
    : items.reduce((sum, item) => sum + parseFloat(String(item.shippingFee || '0')), 0);
  return itemsTotal + buyerShipping;
};

const SELLER_TX_FEE_CUTOFF = new Date('Mon Jan 28 2025 23:59:59 GMT+0400');
const SELLER_GROUND_SHIPPING_CUTOFF = new Date('Mon Feb 20 2025 23:59:59 GMT+0400');

export type SellerPayoutBreakdown = {
  itemsTotal: number;
  transactionFee: number;
  primePostFee: number;
  fedExInsurance: number;
  shippingFeeDeducted: number;
  isCardPayment: boolean;
  capturedAmountForCardFee: number;
  stripeCardFee: number;
  creditTotal: number;
};

/** Seller payout breakdown (matches Confirm Order + transfer job card fee logic). */
export const getSellerPayoutBreakdown = (orders: any[]): SellerPayoutBreakdown | null => {
  const filtered = orders.filter((o) => o.status === 1);
  if (!filtered.length) return null;

  const first = filtered[0];
  const isNewOrder = new Date(first.createdAt) > SELLER_TX_FEE_CUTOFF;

  const itemsTotal = filtered.reduce((s, i) => s + Number(i.totalAmount || 0), 0);
  const primePostFee = filtered.reduce(
    (s, i) => s + (Number(i.totalAmount) * Number(i.primePostFee)) / 100,
    0
  );
  const isCardPayment = isCardPaymentOrder(first);

  const transactionFee = isCardPayment
    ? 0
    : isNewOrder
      ? 10
      : calculateTransactionFee(itemsTotal);

  let fedExInsurance = 0;
  let shippingFeeDeducted = 0;
  filtered.forEach((item) => {
    fedExInsurance += Number(item.fedExInsurance) || 0;
    const itemDate = new Date(item.createdAt);
    const isNewOrderForGround = itemDate > SELLER_GROUND_SHIPPING_CUTOFF;
    const isFedexGround = item.ShippingMethod?.key === 'fedex-ground';
    const isShippingPaidBySeller = Boolean(item.isShippingPaidBySeller);
    if (isShippingPaidBySeller || (isNewOrderForGround && isFedexGround)) {
      shippingFeeDeducted += Number(item.shippingFee) || 0;
    }
  });
  const capturedAmountForCardFee = getBuyerCapturedAmountForCardFee(
    filtered,
    first.ShippingMethod?.key ?? '',
    first.isShippingPaidBySeller === true
  );
  const stripeCardFee = isCardPayment
    ? calculateSellerCardProcessingFee(capturedAmountForCardFee)
    : 0;

  const creditTotal = Math.max(
    0,
    itemsTotal -
      primePostFee -
      transactionFee -
      fedExInsurance -
      shippingFeeDeducted -
      stripeCardFee
  );

  return {
    itemsTotal,
    transactionFee,
    primePostFee,
    fedExInsurance,
    shippingFeeDeducted,
    isCardPayment,
    capturedAmountForCardFee,
    stripeCardFee,
    creditTotal,
  };
};

export const normalizeNdc = (ndc: string): string => {
  // Remove all dashes for comparison (matches backend)
  return ndc.replace(/-/g, '');
};

export const formatNdc = (ndc: string) => {
  if (ndc.length !== 13) {
    const ndcItems = ndc.split('-');
    if (ndcItems[0].length !== 5) {
      ndcItems[0] = `0${ndcItems[0]}`
    }

    if (ndcItems[1].length !== 4) {
      ndcItems[1] = `0${ndcItems[1]}`
    }

    if (ndcItems[2].length !== 2) {
      ndcItems[2] = `0${ndcItems[2]}`
    }

    return ndcItems.join('-');
  }

  return ndc;
}

export const formatSearchNdcNumber = (ndc: string) => {
  const formattedNdc = ndc.trim().replaceAll('-', '');
  const isNumber = /^\d+$/.test(formattedNdc);

  if (isNumber && formattedNdc.length >= 9 && formattedNdc.length <= 11) {
     const correctNdc = formattedNdc.slice(0, 9);

     let firstItem = correctNdc.slice(0, 5);
     let secondItem = correctNdc.slice(5, 9);

     if (firstItem.charAt(0) === '0') {
       firstItem = firstItem.substring(1);
     }
     else {
      if (secondItem.charAt(0) === '0') {
        secondItem = secondItem.substring(1);
      }
     }

     return `${firstItem}${secondItem}`

  } else {
    return formattedNdc;
  }
}

export const filterNdcOptions = (options: any, inputValue: string) => {
  let data: any = [];

  options.forEach((option: any) => {
    if (inputValue !== '') {
      const formattedinputValue = inputValue.trim();
      let firstItem = formattedinputValue.slice(0, 5);
      let secondItem = formattedinputValue.slice(5, 9);
      let thirdItem = formattedinputValue.slice(9, 11);

      if (firstItem.charAt(0) === '0') {
        firstItem = firstItem.substring(1);
      }
      else {
        if (secondItem.charAt(0) === '0') {
          secondItem = secondItem.substring(1);
        }
        else {
          if (thirdItem.charAt(0) === '0') {
            thirdItem = thirdItem.substring(1);
          }
        }
      }
      const foundData = option._source.packaging.find((item: any) => item.package_ndc === `${firstItem}-${secondItem}-${thirdItem}` || item.package_ndc === `${firstItem}-${secondItem}-0${thirdItem}`);

      if (!foundData) {
        return [];
      }

      data = [{
        ...option._source,
        package_ndc: formatNdc(foundData.package_ndc),
        package_description: foundData.description,
      }]
    } else {
      option._source.packaging.forEach((packaging: any) => {
        data.push({
          ...option._source,
          package_ndc: formatNdc(packaging.package_ndc),
          package_description: packaging.description,
        })
      })
    }
  });

  return data;
}

export const getNextBusinessDays: any = (isFrozen: boolean = false) => {
  let firstDayIncrement: number = 1;
  let secondDayIncrement: number = 2;

  // Wednesday
  if (moment().day() === 3) {
    firstDayIncrement = 1;
    secondDayIncrement = isFrozen ? 5 : 2;
  }

  // Thursday
  if (moment().day() === 4) {
    firstDayIncrement = isFrozen ? 4 : 1;
    secondDayIncrement = isFrozen ? 5 : 4;
  }

  // Friday
  if (moment().day() === 5) {
    firstDayIncrement = 3;
    secondDayIncrement = 4;
  }

  // Saturday
  if (moment().day() === 6) {
    firstDayIncrement = 2;
    secondDayIncrement = 3;
  }

  return [
    moment().add(firstDayIncrement, 'd'),
    moment().add(secondDayIncrement, 'd'),
  ]
}

export const getPackTypeDisplay = (type: number, packaging?: string) => {
  if (type === 1) {
    return "Full";
  } else {
    return packaging ? `Partial (count: ${packaging})` : "Partial";
  }
};

export const getSealedDisplay = (packageCondition: number, type?: number) => {
  // For both Full and Partial packages, show sealed/non-sealed information
  return packageCondition === 1 ? "Sealed (See Details)" : "Non-Sealed (See Details)";
};

export const getSealDetailsDisplay = (sealDetails: string) => {
  const sealDetailsMap: { [key: string]: string } = {
    'SEALED_ORIGINAL_CONTAINER': 'Original Container',
    'SEALED_ORIGINAL_BOTTLE_TORN_LABEL': 'Original Bottle/Torn or label residue',
    'OPEN_ORIGINAL_CONTAINER': 'Original Container',
    'OPEN_PRESCRIPTION_BOTTLE': 'Prescription bottle',
    'OPEN_ORIGINAL_BOTTLE_TORN_LABEL': 'Original bottle/Torn on label residue'
  };
  
  return sealDetailsMap[sealDetails] || sealDetails;
};

export const getDisplayProduct = (order: any) => {
  const snapshot = order?.OrderItemSnapshot;
  const product = order?.Product || {};

  if (!snapshot) {
    return product;
  }

  return {
    ...product,
    drugName: snapshot.drugName ?? product.drugName,
    ndc: snapshot.ndc ?? product.ndc,
    labelerName: snapshot.labelerName ?? product.labelerName,
    form: snapshot.form ?? product.form,
    strength: snapshot.strength ?? product.strength,
    lotNumber: snapshot.lotNumber ?? product.lotNumber,
    expDate: snapshot.expDate ?? product.expDate,
    packageCondition: snapshot.packageCondition ?? product.packageCondition,
    packaging: snapshot.packaging ?? product.packaging,
    quantityPerPack: snapshot.quantityPerPack ?? product.quantityPerPack,
    isFrozen: snapshot.isFrozen ?? product.isFrozen,
    sealDetails: snapshot.sealDetails ?? product.sealDetails,
    type: snapshot.type ?? product.type,
    unitPrice: snapshot.unitPriceSnapshot ?? product.unitPrice,
    price: snapshot.listPriceSnapshot ?? product.price,
  };
};